import { FC, ReactNode } from 'react';
import { Mobile } from '../../../../../packages/ui/components/responsive';
import {
  CollapsableContent,
  LeaderboardRowMobile,
  RankCell,
} from '../../../../../packages/ui/features/leaderboard/leaderboard-row';
import EmptyLeaderboardBody from './empty-leaderboard-body';
import { cn } from '../../../../../packages/ui/lib/lib';
import {
  LeaderboardDesktop,
  LeaderboardMobile,
} from '../../../../../packages/ui/features/leaderboard/leaderboard';
import { PlayerLeaderboardSkeleton } from './player-leaderboard';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../../../packages/ui/components/arccordion';
import { Accordion } from '../../../../../packages/ui/components/arccordion';
import { tryFormatThousands } from '@wwe-portal/utils/format';

export interface FactionEventLeaderboardProps {
  mobileHeader: ReactNode;
  desktopHeader: ReactNode;
  data: {
    points: number;
    rank: number;
    event: string;
    endDate: string;
  }[];
}

const RowLayout: FC<{
  rank: ReactNode;
  event: ReactNode;
  points: ReactNode;
  endDate: ReactNode;
  className: string;
}> = ({ rank, event, points, endDate, className }) => {
  return (
    <div
      className={cn(
        'grid min-h-[52px] grid-cols-[70px_repeat(11,minmax(0,_1fr))] p-5',
        className
      )}
    >
      <div className={cn('col-span-2')}>{rank}</div>
      <div className={cn('col-span-5')}>{event}</div>
      <div className={cn('col-span-3')}>
        <div className="text-xs no-underline hover:no-underline">{endDate}</div>
      </div>
      <div className={cn('col-span-2 text-right')}>{points}</div>
    </div>
  );
};

const FactionEventLeaderboardDesktop: FC<
  Omit<FactionEventLeaderboardProps, 'mobileHeader' | 'mobileRowHeader'>
> = ({ data, desktopHeader }) => {
  const isEmpty = data.length === 0;
  return (
    <LeaderboardDesktop
      header={desktopHeader}
      rows={
        isEmpty ? (
          <EmptyLeaderboardBody />
        ) : (
          <>
            <RowLayout
              className="text-text700 text-xs font-semibold uppercase"
              points={'points'}
              rank={'rank'}
              endDate={'end date'}
              event="event"
            />
            {data.map((item, index) => (
              <div className={cn('even:bg-back400 rounded-xl')} key={index}>
                <RowLayout
                  className="text-text300 text-xs"
                  points={tryFormatThousands(item.points)}
                  rank={<RankCell rank={item.rank} />}
                  endDate={item.endDate}
                  event={item.event}
                />
              </div>
            ))}
          </>
        )
      }
    />
  );
};

const FactionEventLeaderboardMobile: FC<
  Omit<FactionEventLeaderboardProps, 'desktopHeader' | 'desktopRowHeader'>
> = ({ data, mobileHeader }) => {
  const isEmpty = data.length === 0;
  return (
    <LeaderboardMobile
      header={mobileHeader}
      rows={
        isEmpty ? (
          <EmptyLeaderboardBody />
        ) : (
          <>
            <Accordion type="single" collapsible>
              {data.map((item, index) => (
                <AccordionItem
                  value={index.toString()}
                  key={index}
                  className={cn(
                    'data-[me=true]:bg-borderColor7 data-[me=true]:border-borderColor15 rounded-xl odd:bg-[#3d426e] data-[me=true]:border',
                    {
                      'border-back200 rounded-xl border border-solid':
                        item.rank === 1,
                    }
                  )}
                >
                  <AccordionTrigger>
                    <LeaderboardRowMobile
                      data={item}
                      title={item.event}
                      subtitle={item.endDate}
                      className="text-xs font-normal"
                    />
                  </AccordionTrigger>
                  <AccordionContent>
                    <CollapsableContent
                      points={item.points}
                      endDate={item.endDate}
                      expanded
                    />
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </>
        )
      }
    />
  );
};

export const HistoryEventLeaderboard: FC<FactionEventLeaderboardProps> = (
  props
) => {
  return (
    <Mobile
      fallback={<FactionEventLeaderboardDesktop {...props} />}
      loading={
        <PlayerLeaderboardSkeleton
          compact={false}
          desktopHeader={props.desktopHeader}
          mobileHeader={props.mobileHeader}
        />
      }
    >
      <FactionEventLeaderboardMobile
        data={props.data}
        mobileHeader={props.mobileHeader}
      />
    </Mobile>
  );
};
